import React, { useState, useEffect } from 'react';
import { COUNTRIES, getStateOptions } from '../../constants/countries';

export interface AddressData {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface AddressFormProps {
  onAddressChange: (address: AddressData) => void;
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
}

// Countries where service is not available due to tax regulations
const BLOCKED_COUNTRIES = ['GB'];

const BLOCKED_COUNTRY_MESSAGE: Record<string, { title: string; message: string }> = {
  'GB': {
    title: 'Service Not Available in the United Kingdom',
    message: 'Unfortunately, we cannot currently accept subscriptions from the United Kingdom due to complex tax regulations. We apologize for the inconvenience and hope to expand our service to the UK in the future.'
  }
};



const AddressForm: React.FC<AddressFormProps> = ({ onAddressChange, isValid, onValidationChange }) => {
  const [address, setAddress] = useState<AddressData>({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US', // Default to US
  });

  const [errors, setErrors] = useState<Partial<AddressData>>({});

  // Check if currently selected country is blocked
  const isBlockedCountry = BLOCKED_COUNTRIES.includes(address.country);
  const blockedCountryInfo = isBlockedCountry ? BLOCKED_COUNTRY_MESSAGE[address.country] : null;

  // When country changes to/from blocked, update parent validation state
  useEffect(() => {
    if (isBlockedCountry) {
      onValidationChange(false);
    }
  }, [isBlockedCountry, onValidationChange]);

  const validateField = (name: keyof AddressData, value: string): string => {
    switch (name) {
      case 'address_line1':
        return value.trim() ? '' : 'Street address is required';
      case 'city':
        return value.trim() ? '' : 'City is required';
      case 'state':
        return value.trim() ? '' : (address.country === 'US' || address.country === 'CA' ? 'State/Province is required' : '');
      case 'postal_code':
        if (!value.trim()) return 'Postal code is required';
        // Basic format validation
        if (address.country === 'US') {
          return /^\d{5}(-\d{4})?$/.test(value) ? '' : 'Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)';
        }
        if (address.country === 'CA') {
          return /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(value) ? '' : 'Please enter a valid Canadian postal code (e.g., K1A 0A6)';
        }
        return ''; // For other countries, just require non-empty
      case 'country':
        return value ? '' : 'Country is required';
      default:
        return '';
    }
  };

  const validateForm = (updatedAddress: AddressData): boolean => {
    // If country is blocked, form is always invalid
    if (BLOCKED_COUNTRIES.includes(updatedAddress.country)) {
      setErrors({});
      return false;
    }

    const newErrors: Partial<AddressData> = {};
    let hasErrors = false;

    // Validate required fields
    Object.keys(updatedAddress).forEach((key) => {
      const fieldName = key as keyof AddressData;
      const error = validateField(fieldName, updatedAddress[fieldName] || '');
      if (error) {
        newErrors[fieldName] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleInputChange = (name: keyof AddressData, value: string) => {
    const updatedAddress = { ...address, [name]: value };
    
    // Clear state when country changes to non-US/CA
    if (name === 'country' && value !== 'US' && value !== 'CA') {
      updatedAddress.state = '';
    }
    
    setAddress(updatedAddress);
    
    // Validate and notify parent
    const isFormValid = validateForm(updatedAddress);
    onValidationChange(isFormValid);
    onAddressChange(updatedAddress);
  };



  const shouldShowState = address.country === 'US' || address.country === 'CA';

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-700 pb-4 mb-6">
        <h3 className="text-xl font-semibold text-white mb-2 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Billing Address
        </h3>
        <p className="text-sm text-gray-400">
          We need your address to calculate applicable taxes for your location
        </p>
      </div>
      
      {/* Country Selection */}
      <div className="relative">
        <label htmlFor="country" className="block text-sm font-semibold text-gray-300 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Country *
        </label>
        <div className="relative">
          <select
            id="country"
            value={address.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                     transition-all duration-200 appearance-none cursor-pointer
                     hover:border-gray-500 text-base"
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code} className="bg-gray-800 text-white">
                {country.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {errors.country && (
          <p className="text-red-400 text-sm mt-2 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.country}
          </p>
        )}
      </div>

      {/* Blocked Country Warning */}
      {isBlockedCountry && blockedCountryInfo && (
        <div className="bg-amber-900/40 border border-amber-500/60 rounded-xl p-5 animate-fadeIn">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold text-amber-300">
                {blockedCountryInfo.title}
              </h4>
              <p className="mt-2 text-amber-200/90 text-sm leading-relaxed">
                {blockedCountryInfo.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Only show address fields if country is not blocked */}
      {!isBlockedCountry && (
        <>
          {/* Address Line 1 */}
          <div>
            <label htmlFor="address_line1" className="block text-sm font-semibold text-gray-300 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Street Address *
            </label>
            <input
              id="address_line1"
              type="text"
              value={address.address_line1}
              onChange={(e) => handleInputChange('address_line1', e.target.value)}
              placeholder="123 Main Street"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                       placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                       transition-all duration-200 hover:border-gray-500 text-base"
            />
            {errors.address_line1 && (
              <p className="text-red-400 text-sm mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {errors.address_line1}
              </p>
            )}
          </div>

          {/* Address Line 2 */}
          <div>
            <label htmlFor="address_line2" className="block text-sm font-semibold text-gray-300 mb-3">
              Apartment, suite, etc. <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              id="address_line2"
              type="text"
              value={address.address_line2}
              onChange={(e) => handleInputChange('address_line2', e.target.value)}
              placeholder="Apt 4B, Floor 2, etc."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                       placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                       transition-all duration-200 hover:border-gray-500 text-base"
            />
          </div>

          {/* City and State/Province */}
          <div className={`grid gap-6 ${shouldShowState ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-gray-300 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                City *
              </label>
              <input
                id="city"
                type="text"
                value={address.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="New York"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                         placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         transition-all duration-200 hover:border-gray-500 text-base"
              />
              {errors.city && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {errors.city}
                </p>
              )}
            </div>

            {shouldShowState && (
              <div className="relative">
                <label htmlFor="state" className="block text-sm font-semibold text-gray-300 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  {address.country === 'US' ? 'State' : 'Province'} *
                </label>
                <div className="relative">
                  <select
                    id="state"
                    value={address.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                             transition-all duration-200 appearance-none cursor-pointer
                             hover:border-gray-500 text-base"
                  >
                    <option value="" className="bg-gray-800 text-gray-400">
                      Select {address.country === 'US' ? 'State' : 'Province'}
                    </option>
                    {getStateOptions(address.country).map((state) => (
                      <option key={state} value={state} className="bg-gray-800 text-white">
                        {state}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.state && (
                  <p className="text-red-400 text-sm mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.state}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Postal Code */}
          <div>
            <label htmlFor="postal_code" className="block text-sm font-semibold text-gray-300 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {address.country === 'US' ? 'ZIP Code' : 'Postal Code'} *
            </label>
            <input
              id="postal_code"
              type="text"
              value={address.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              placeholder={address.country === 'US' ? '12345' : address.country === 'CA' ? 'K1A 0A6' : 'Postal code'}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                       placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                       transition-all duration-200 hover:border-gray-500 text-base"
            />
            {errors.postal_code && (
              <p className="text-red-400 text-sm mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {errors.postal_code}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AddressForm;